import type { ContactField } from '@heybesafe/shared';
import { HbsInput } from './hbs/Input';
import { HbsButton } from './hbs/Button';

interface Props {
  /** Prefix for this group's buttons: `<idPrefix>-add` and, per row,
   *  `<idPrefix>-remove-<i>`. The form renders this component once per field
   *  group, so the parent supplies a distinct prefix for each. */
  idPrefix?: string;
  legend: string;
  values: ContactField[];
  onChange: (next: ContactField[]) => void;
  /** Suggested labels offered via a datalist (e.g. mobile / home / work). */
  labelSuggestions?: string[];
  valuePlaceholder?: string;
  /** Input type for the value field — drives the mobile keyboard + validation. */
  valueType?: 'text' | 'email' | 'tel';
  addLabel: string;
}

/**
 * A repeatable list of labelled contact details — phones, emails, or addresses.
 * Each row is a free-text label (with suggestions) plus a value. Blank rows are
 * the caller's to drop on save (mirrors CustomFieldListField).
 */
export default function ContactFieldListField({
  idPrefix,
  legend,
  values,
  onChange,
  labelSuggestions = [],
  valuePlaceholder,
  valueType = 'text',
  addLabel,
}: Props): JSX.Element {
  const datalistId = `contact-labels-${legend.replace(/\s+/g, '-').toLowerCase()}`;

  function updateAt(i: number, next: ContactField): void {
    const copy = [...values];
    copy[i] = next;
    onChange(copy);
  }

  function removeAt(i: number): void {
    onChange(values.filter((_, j) => j !== i));
  }

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-xs font-medium text-hbs-text-mute uppercase tracking-hbs-overline">
        {legend}
      </legend>
      {labelSuggestions.length > 0 && (
        <datalist id={datalistId}>
          {labelSuggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      )}
      {values.map((field, i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="w-[110px] flex-shrink-0">
            <input
              type="text"
              list={datalistId}
              aria-label={`${legend} label ${i + 1}`}
              value={field.label}
              placeholder="Label"
              onChange={(e) => updateAt(i, { ...field, label: e.target.value })}
              className="w-full h-12 px-3 bg-hbs-surface border-[1.5px] border-hbs-border rounded-hbs-sm text-[15px] text-hbs-text placeholder:text-hbs-text-mute focus:outline-none focus-within:border-hbs-primary focus-within:ring-4 focus-within:ring-hbs-primary/20"
            />
          </div>
          <div className="flex-1">
            <HbsInput
              type={valueType}
              value={field.value}
              placeholder={valuePlaceholder}
              onChange={(value) => updateAt(i, { ...field, value })}
            />
          </div>
          <button
            id={idPrefix ? `${idPrefix}-remove-${i}` : undefined}
            type="button"
            aria-label={field.label ? `Remove ${field.label}` : `Remove ${legend} row`}
            onClick={() => removeAt(i)}
            className="shrink-0 mt-3.5 p-1 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt"
          >
            ×
          </button>
        </div>
      ))}
      <HbsButton
        id={idPrefix ? `${idPrefix}-add` : undefined}
        variant="ghost"
        type="button"
        onClick={() => onChange([...values, { label: '', value: '' }])}
      >
        {addLabel}
      </HbsButton>
    </fieldset>
  );
}
